import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// ── Module metadata ───────────────────────────────────────────────────────────
const TEACHER_MODULES = [
  { key:'dashboard',        label:'Dashboard',         icon:'🏠', desc:'Teacher home with stats and charts' },
  { key:'classes',          label:'My Classes',         icon:'👥', desc:'View assigned classes and students' },
  { key:'attendance',       label:'My Attendance',      icon:'📅', desc:'Check-in/out and own attendance records' },
  { key:'assessments',      label:'Assessments',        icon:'📝', desc:'Create and manage student assessments' },
  { key:'gradeReports',     label:'Grade Reports',      icon:'📊', desc:'View student grade reports' },
  { key:'studentAttendance',label:'Student Attendance', icon:'✅', desc:'Mark and view student attendance' },
  { key:'datesheets',       label:'Datesheets',         icon:'🗓️', desc:'View exam datesheets' },
  { key:'materials',        label:'Materials',          icon:'📚', desc:'Upload and manage teaching materials' },
  { key:'quizzes',          label:'AI Quizzes',         icon:'🤖', desc:'Create AI and manual quizzes' },
  { key:'notices',          label:'Notices',            icon:'📢', desc:'View and post notices' },
  { key:'salary',           label:'Salary',             icon:'💰', desc:'View own salary and history' },
  { key:'leaves',           label:'Leaves',             icon:'🏖️', desc:'Apply and manage leaves' },
  { key:'profile',          label:'Profile',            icon:'👤', desc:'View and update profile' },
];

const STUDENT_MODULES = [
  { key:'dashboard',   label:'Dashboard',          icon:'🏠', desc:'Student home with quick stats' },
  { key:'attendance',  label:'Attendance',          icon:'📅', desc:'View attendance records and apply leave' },
  { key:'results',     label:'Results',             icon:'🎯', desc:'View marks and grade reports' },
  { key:'fees',        label:'Fees',                icon:'💵', desc:'View fee bills and submit payment proof' },
  { key:'materials',   label:'Materials',           icon:'📚', desc:'Access study materials by subject' },
  { key:'quizzes',     label:'Online Assessments',  icon:'📋', desc:'Attempt assigned quizzes' },
  { key:'notices',     label:'Notices',             icon:'📢', desc:'View school and teacher notices' },
  { key:'timetable',   label:'Timetable',           icon:'🕐', desc:'View class timetable with times' },
  { key:'datesheets',  label:'Datesheets',          icon:'🗓️', desc:'View exam schedules' },
  { key:'profile',     label:'Profile',             icon:'👤', desc:'View and update profile' },
];

const FINANCE_MODULES = [
  { key:'dashboard',       label:'Dashboard',        icon:'🏠', desc:'Finance home with stats and charts' },
  { key:'attendance',      label:'Attendance',       icon:'📅', desc:'Own check-in/out and attendance records' },
  { key:'leaves',          label:'Leaves',           icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'staffAttendance', label:'Staff Attendance', icon:'✅', desc:'View staff attendance records' },
  { key:'staffLeaves',     label:'Staff Leaves',     icon:'📋', desc:'Review staff leave requests' },
  { key:'staffManagement', label:'Staff Management', icon:'🏢', desc:'Manage staff accounts' },
  { key:'fees',            label:'Fees',             icon:'💵', desc:'Manage student fee bills and payments' },
  { key:'staffSalary',     label:'Staff Salary',     icon:'📊', desc:'View and manage staff salary records' },
  { key:'mySalary',        label:'My Salary',        icon:'💰', desc:'View own salary and history' },
  { key:'expenses',        label:'Expenses',         icon:'💲', desc:'Track school expenses' },
  { key:'assets',          label:'Assets',           icon:'📦', desc:'Track school assets' },
  { key:'biometric',       label:'Biometric & RFID', icon:'🔒', desc:'Manage biometric and RFID devices' },
  { key:'studentGateLog',  label:'Student Gate Log', icon:'🪪', desc:'View student RFID gate logs' },
  { key:'certificates',    label:'Certificates',     icon:'📄', desc:'Generate event certificates and staff letters' },
  { key:'notices',         label:'Notices',          icon:'📢', desc:'View and post notices' },
  { key:'requests',        label:'My Requests',      icon:'📥', desc:'Track submitted requests' },
  { key:'profile',         label:'Profile',          icon:'👤', desc:'View and update profile' },
];

const COORDINATOR_MODULES = [
  { key:'dashboard',         label:'Dashboard',          icon:'🏠', desc:'Coordinator home with stats' },
  { key:'students',          label:'Students',           icon:'👥', desc:'View and manage students' },
  { key:'teachers',          label:'Teachers',           icon:'🧑‍🏫', desc:'View and manage teachers' },
  { key:'classes',           label:'Classes',            icon:'🏫', desc:'View and manage classes' },
  { key:'studentAttendance', label:'Student Attendance', icon:'✅', desc:'Mark and view student attendance' },
  { key:'myAttendance',      label:'My Attendance',      icon:'📅', desc:'Check-in/out and own attendance' },
  { key:'studentLeft',       label:'Student Left',       icon:'🚪', desc:'View students who have left' },
  { key:'staffExits',        label:'Staff Exits',        icon:'🚪', desc:'View and request staff exits across all roles' },
  { key:'classPromotion',    label:'Class Promotion',    icon:'🎓', desc:'Promote students to next class' },
  { key:'yearEndCleanup',    label:'Year-End Cleanup',   icon:'🗑️', desc:'Archive year-end data' },
  { key:'salary',            label:'Salary',             icon:'📊', desc:'View teacher salary records' },
  { key:'myLeaves',          label:'My Leaves',          icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'holidays',          label:'Holidays',           icon:'📆', desc:'View and manage holidays' },
  { key:'materials',         label:'Materials',          icon:'📚', desc:'View teaching materials' },
  { key:'assessmentTypes',   label:'Assessment Types',   icon:'📝', desc:'Manage assessment types' },
  { key:'assessments',       label:'Assessments',        icon:'📝', desc:'View and manage assessments' },
  { key:'gradeReports',      label:'Grade Reports',      icon:'📊', desc:'View student grade reports' },
  { key:'timetable',         label:'Timetable',          icon:'🕐', desc:'View class timetable' },
  { key:'notices',           label:'Notices',            icon:'📢', desc:'View and post notices' },
  { key:'feedback',          label:'Feedback',           icon:'📈', desc:'View feedback results' },
  { key:'cloManagement',     label:'CLO Management',     icon:'📈', desc:'Manage course learning outcomes' },
  { key:'attainment',        label:'CLO Attainment',     icon:'📈', desc:'View CLO attainment' },
  { key:'ploManagement',     label:'PLO Management',     icon:'📈', desc:'View program learning outcomes' },
  { key:'ploAttainment',     label:'PLO Attainment',     icon:'📈', desc:'View PLO attainment' },
  { key:'myRequests',        label:'My Requests',        icon:'📥', desc:'Track submitted requests' },
  { key:'accessPanel',       label:'Access Panel',       icon:'🛡️', desc:'Manage teacher/student access requests' },
  { key:'profile',           label:'Profile',            icon:'👤', desc:'View and update profile' },
];

const LIBRARIAN_MODULES = [
  { key:'dashboard',    label:'Dashboard',    icon:'🏠', desc:'Librarian home with stats and charts' },
  { key:'catalog',      label:'Book Catalog', icon:'📚', desc:'Manage the book catalog' },
  { key:'issueReturn',  label:'Issue / Return', icon:'🔄', desc:'Issue and return books' },
  { key:'fines',        label:'Fines',        icon:'💵', desc:'Manage library fines' },
  { key:'myAttendance', label:'My Attendance',icon:'📅', desc:'Own check-in/out and attendance records' },
  { key:'mySalary',     label:'My Salary',    icon:'💰', desc:'View own salary and history' },
  { key:'myLeaves',     label:'My Leaves',    icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'profile',      label:'Profile',      icon:'👤', desc:'View and update profile' },
];

const HOSTEL_MODULES = [
  { key:'dashboard',    label:'Dashboard',    icon:'🏠', desc:'Hostel home with stats and charts' },
  { key:'rooms',        label:'Rooms',        icon:'🏨', desc:'Manage hostel rooms' },
  { key:'allocations',  label:'Allocation',   icon:'🛏️', desc:'Allocate and vacate students' },
  { key:'fines',        label:'Fines',        icon:'💵', desc:'Manage hostel fines' },
  { key:'myAttendance', label:'My Attendance',icon:'📅', desc:'Own check-in/out and attendance records' },
  { key:'mySalary',     label:'My Salary',    icon:'💰', desc:'View own salary and history' },
  { key:'myLeaves',     label:'My Leaves',    icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'profile',      label:'Profile',      icon:'👤', desc:'View and update profile' },
];

const MODULE_DEFS = { teacher: TEACHER_MODULES, student: STUDENT_MODULES, finance: FINANCE_MODULES, coordinator: COORDINATOR_MODULES, librarian: LIBRARIAN_MODULES, hostel_member: HOSTEL_MODULES };
const ROLE_FIELD   = { teacher: 'teacherModules', student: 'studentModules', finance: 'financeModules', coordinator: 'coordinatorModules', librarian: 'librarianModules', hostel_member: 'hostelModules' };
const ROLE_LABEL   = { teacher: 'Teachers', student: 'Students', finance: 'Finance', coordinator: 'Coordinators', librarian: 'Librarians', hostel_member: 'Hostel Members' };

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ap * { box-sizing:border-box; }
  .ap { font-family:'Sora',sans-serif; color:#1a1d2e; animation:apFade .4s ease both; }
  @keyframes apFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .ap-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ap-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ap-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }

  /* Tabs */
  .ap-tabs { display:flex; gap:4px; background:#f0f2f7; border-radius:12px; padding:4px; margin-bottom:1.4rem; width:fit-content; }
  .ap-tab  { padding:8px 20px; border-radius:9px; border:none; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; white-space:nowrap; }
  .ap-tab.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.12); }

  /* Sub tabs */
  .sub-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .sub-tab  { padding:6px 16px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; transition:all .15s; color:#6b7194; }
  .sub-tab:hover  { border-color:#3b6ef8; color:#3b6ef8; }
  .sub-tab.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }

  /* Search + filter bar */
  .filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:1.2rem; align-items:center; }
  .search-wrap { position:relative; flex:1; min-width:200px; }
  .search-wrap svg { position:absolute; left:10px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; stroke-width:2; }
  .search-input { width:100%; padding:9px 12px 9px 34px; border:1.5px solid #e0e4f0; border-radius:10px; font-family:'Sora',sans-serif; font-size:.82rem; background:#f8f9fc; color:#1a1d2e; outline:none; }
  .search-input:focus { border-color:#3b6ef8; background:white; }
  .filter-select { padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; font-family:'Sora',sans-serif; font-size:.82rem; background:#f8f9fc; color:#1a1d2e; outline:none; }
  .filter-select:focus { border-color:#3b6ef8; }

  /* Buttons */
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 14px; border:none; border-radius:9px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:14px; height:14px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  /* Role defaults card */
  .role-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1rem; }
  .role-card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:1rem; flex-wrap:wrap; gap:8px; }
  .role-card-title { font-size:.9rem; font-weight:700; color:#0f1123; }
  .role-card-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }

  /* Module toggle grid */
  .module-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:10px; }
  .module-item { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-radius:10px; border:1.5px solid #e8ebf4; background:#fafbff; gap:8px; transition:all .15s; }
  .module-item.enabled  { border-color:#10c98e; background:#f0fdf8; }
  .module-item.disabled { border-color:#e8ebf4; background:#fafbff; opacity:.75; }
  .module-item-left { display:flex; align-items:center; gap:10px; min-width:0; }
  .module-icon  { font-size:1.1rem; flex-shrink:0; }
  .module-label { font-size:.8rem; font-weight:600; color:#0f1123; }
  .module-desc  { font-size:.68rem; color:#9094a8; margin-top:1px; }

  /* Toggle switch */
  .toggle { position:relative; width:40px; height:22px; flex-shrink:0; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; cursor:pointer; inset:0; background:#d1d5db; border-radius:99px; transition:.2s; }
  .toggle-slider:before { content:''; position:absolute; width:16px; height:16px; left:3px; top:3px; background:white; border-radius:50%; transition:.2s; }
  .toggle input:checked + .toggle-slider { background:#10c98e; }
  .toggle input:checked + .toggle-slider:before { transform:translateX(18px); }

  /* Person list */
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }

  .person-row { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; }
  .person-row:last-child { border-bottom:none; }
  .person-row:hover { background:#fafbff; }
  .p-avatar { width:32px; height:32px; border-radius:9px; background:#eef2ff; color:#3b6ef8; display:flex; align-items:center; justify-content:center; font-size:.68rem; font-weight:700; flex-shrink:0; }
  .p-name  { font-size:.82rem; font-weight:600; color:#0f1123; }
  .p-meta  { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .p-badge { display:inline-flex; font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .p-badge.override  { background:#fef3c7; color:#92400e; }
  .p-badge.default   { background:#f0f2f7; color:#6b7280; }
  .p-badge.class-ov  { background:#ede9fe; color:#7c3aed; }
  .p-badge.section-ov{ background:#dbeafe; color:#1d4ed8; }

  /* Override drawer */
  .override-drawer { background:#f8f9fc; border-top:1px solid #f0f2f8; padding:14px 18px; }
  .od-title { font-size:.76rem; font-weight:700; color:#1a1d2e; margin-bottom:10px; }
  .od-module-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:8px; margin-bottom:10px; }
  .od-module { display:flex; align-items:center; gap:8px; padding:7px 10px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; font-size:.76rem; font-weight:500; cursor:pointer; transition:all .13s; user-select:none; }
  .od-module.on  { border-color:#10c98e; background:#f0fdf8; color:#0f1123; }
  .od-module.off { border-color:#e0e4f0; background:white; color:#9094a8; }
  .od-module .dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .od-module.on  .dot { background:#10c98e; }
  .od-module.off .dot { background:#d1d5db; }
  .od-actions { display:flex; gap:8px; }

  /* Class override section */
  .class-section { background:white; border-radius:14px; border:1px solid #e8ebf4; margin-bottom:10px; overflow:hidden; }
  .class-section-head { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; cursor:pointer; gap:8px; flex-wrap:wrap; }
  .class-section-head:hover { background:#fafbff; }
  .cs-name { font-size:.84rem; font-weight:700; color:#0f1123; }
  .cs-meta { font-size:.7rem; color:#9094a8; margin-top:2px; }

  .ap-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .ap-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  @media (max-width:768px) {
    .ap-title { font-size:1.1rem; }
    .ap-tabs { width:100%; }
    .ap-tab  { flex:1; text-align:center; }
    .module-grid { grid-template-columns:1fr; }
    .filter-bar { flex-direction:column; }
    .search-wrap { width:100%; }
    .filter-select { width:100%; }
    .od-module-grid { grid-template-columns:1fr 1fr; }
  }
`;

const inits = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';

// FIX: format assignedClasses array (objects or strings) into readable text
const fmtClasses = (assignedClasses) => {
  if (!assignedClasses || !Array.isArray(assignedClasses) || assignedClasses.length === 0)
    return 'No classes';
  return assignedClasses.map(c => {
    if (typeof c === 'string') return c;
    if (c && typeof c === 'object') return c.name || c.className || String(c.classId || '');
    return '';
  }).filter(Boolean).join(', ');
};

// ─────────────────────────────────────────────────────────────────────────────
const AccessPanel = () => {
  const [tab,        setTab]        = useState('teacher'); // teacher | student
  const [subTab,     setSubTab]     = useState('role');    // role | class | individual
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [access,     setAccess]     = useState(null);
  const [classes,    setClasses]    = useState([]);
  const [teachers,   setTeachers]   = useState([]);
  const [students,   setStudents]   = useState([]);
  const [financeMembers,   setFinanceMembers]   = useState([]);
  const [coordinatorsList, setCoordinatorsList] = useState([]);
  const [librariansList,   setLibrariansList]   = useState([]);
  const [hostelMembersList,setHostelMembersList]= useState([]);
  const [search,     setSearch]     = useState('');
  const [filterClass,setFilterClass]= useState('');
  const [filterSec,  setFilterSec]  = useState('');
  const [openDrawer, setOpenDrawer] = useState(null); // userId
  const [draftModules, setDraftModules] = useState({}); // userId -> modules[]
  const [openClass,  setOpenClass]  = useState(null);  // classId+section key
  const [classDraft, setClassDraft] = useState({});    // key -> modules[]

  const modules = MODULE_DEFS[tab] || [];

  // Load access settings and classes
  useEffect(() => {
    Promise.all([
      api.get('/admin/access'),
      api.get('/admin/classes'),
    ]).then(([a, c]) => {
      setAccess(a.data);
      setClasses(c.data || []);
    }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  // Load teachers/students when needed
  // NOTE: for teachers, filterSec is NOT passed to API and does NOT trigger refetch
  // Section filtering for teachers is done on frontend to keep sections dropdown stable
  useEffect(() => {
    if (subTab !== 'individual') return;
    if (tab === 'student') {
      // Students: pass section to API, refetch on section change
      setLoading(true);
      api.get(`/admin/access/students?classId=${filterClass}&section=${filterSec}&_=${Date.now()}`)
        .then(r => setStudents(r.data))
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [tab, subTab, filterClass, filterSec]);

  useEffect(() => {
    if (subTab !== 'individual') return;
    if (tab === 'teacher') {
      // Teachers: never pass section to API, only refetch on class change
      setLoading(true);
      api.get(`/admin/access/teachers?classId=${filterClass}&_=${Date.now()}`)
        .then(r => setTeachers(r.data))
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [tab, subTab, filterClass]); // filterSec intentionally excluded for teachers

  // Finance/coordinator — flat list, no class scoping, refetch only on tab/subTab change
  useEffect(() => {
    if (subTab !== 'individual') return;
    if (tab === 'finance') {
      setLoading(true);
      api.get(`/admin/finance-members?_=${Date.now()}`)
        .then(r => setFinanceMembers(Array.isArray(r.data) ? r.data : []))
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [tab, subTab]);

  useEffect(() => {
    if (subTab !== 'individual') return;
    if (tab === 'coordinator') {
      setLoading(true);
      api.get(`/admin/coordinators?_=${Date.now()}`)
        .then(r => setCoordinatorsList(Array.isArray(r.data) ? r.data : []))
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [tab, subTab]);

  useEffect(() => {
    if (subTab !== 'individual') return;
    if (tab === 'librarian') {
      setLoading(true);
      api.get(`/admin/librarians?_=${Date.now()}`)
        .then(r => setLibrariansList(Array.isArray(r.data) ? r.data : []))
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [tab, subTab]);

  useEffect(() => {
    if (subTab !== 'individual') return;
    if (tab === 'hostel_member') {
      setLoading(true);
      api.get(`/admin/hostel-members?_=${Date.now()}`)
        .then(r => setHostelMembersList(Array.isArray(r.data) ? r.data : []))
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [tab, subTab]);

  // ── Role-level toggles ──────────────────────────────────────────────────────
  const roleModules = access ? (access[ROLE_FIELD[tab]] || []) : modules.map(m => m.key);

  const toggleRoleModule = async (key) => {
    const current = roleModules;
    const updated  = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    try {
      const { data } = await api.put('/admin/access/role', { role: tab, modules: updated });
      setAccess(prev => ({ ...prev, teacherModules: data.teacherModules, studentModules: data.studentModules, financeModules: data.financeModules, coordinatorModules: data.coordinatorModules, librarianModules: data.librarianModules, hostelModules: data.hostelModules }));
      toast.success('Access updated');
    } catch { toast.error('Failed to update'); }
  };

  const setAllRole = async (enable) => {
    const updated = enable ? modules.map(m => m.key) : [];
    try {
      const { data } = await api.put('/admin/access/role', { role: tab, modules: updated });
      setAccess(prev => ({ ...prev, teacherModules: data.teacherModules, studentModules: data.studentModules, financeModules: data.financeModules, coordinatorModules: data.coordinatorModules, librarianModules: data.librarianModules, hostelModules: data.hostelModules }));
      toast.success(enable ? 'All modules enabled' : 'All modules disabled');
    } catch { toast.error('Failed'); }
  };

  // ── Class overrides ─────────────────────────────────────────────────────────
  const getClassOverride = (classId, section) =>
    access?.classOverrides?.find(o => String(o.class) === String(classId) && o.section === (section||''));

  const saveClassOverride = async (classId, section, mods) => {
    try {
      const { data } = await api.put('/admin/access/class', { classId, section: section||'', modules: mods });
      setAccess(prev => ({ ...prev, classOverrides: data.classOverrides }));
      toast.success('Class override saved');
    } catch { toast.error('Failed'); }
  };

  const removeClassOverride = async (classId, section) => {
    try {
      const { data } = await api.delete('/admin/access/class', { data: { classId, section: section||'' } });
      setAccess(prev => ({ ...prev, classOverrides: data.classOverrides }));
      toast.success('Override removed — using role defaults');
    } catch { toast.error('Failed'); }
  };

  // ── User overrides ──────────────────────────────────────────────────────────
  const openUserDrawer = (userId, currentModules) => {
    if (openDrawer === userId) { setOpenDrawer(null); return; }
    setOpenDrawer(userId);
    setDraftModules(prev => ({ ...prev, [userId]: currentModules || [...roleModules] }));
  };

  const toggleDraftModule = (userId, key) => {
    setDraftModules(prev => {
      const cur = prev[userId] || [];
      return { ...prev, [userId]: cur.includes(key) ? cur.filter(k=>k!==key) : [...cur, key] };
    });
  };

  // Only teacher/student list endpoints embed hasOverride/modules server-side —
  // those need a refetch after a change. Finance/coordinator overlay override
  // info client-side from access.userOverrides (updated below already), so no refetch needed.
  const refreshPeopleListIfNeeded = async () => {
    if (tab === 'teacher') {
      const r = await api.get(`/admin/access/teachers?classId=${filterClass}&_=${Date.now()}`);
      setTeachers(r.data);
    } else if (tab === 'student') {
      const r = await api.get(`/admin/access/students?classId=${filterClass}&section=${filterSec}&_=${Date.now()}`);
      setStudents(r.data);
    }
  };

  const saveUserOverride = async (userId) => {
    try {
      setSaving(true);
      const { data } = await api.put('/admin/access/user', { userId, modules: draftModules[userId] });
      setAccess(prev => ({ ...prev, userOverrides: data.userOverrides }));
      await refreshPeopleListIfNeeded();
      toast.success('Override saved');
      setOpenDrawer(null);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const removeUserOverride = async (userId) => {
    try {
      const { data } = await api.delete('/admin/access/user', { data: { userId } });
      setAccess(prev => ({ ...prev, userOverrides: data.userOverrides }));
      await refreshPeopleListIfNeeded();
      toast.success('Override removed — using defaults');
      setOpenDrawer(null);
    } catch { toast.error('Failed'); }
  };

  // ── Filtered people list ────────────────────────────────────────────────────
  const rawPeople = tab === 'teacher' ? teachers : tab === 'student' ? students : tab === 'finance' ? financeMembers : tab === 'coordinator' ? coordinatorsList : tab === 'librarian' ? librariansList : tab === 'hostel_member' ? hostelMembersList : [];
  const people = rawPeople.map(p => {
    if (tab === 'teacher' || tab === 'student') return p; // already normalised server-side
    // Finance/coordinator — overlay override info client-side (their list endpoints don't include it)
    const userId   = String(p.user_id || p.userId || p.user?._id || p._id);
    const override = access?.userOverrides?.find(o => String(o.user) === userId);
    return {
      ...p,
      userId,
      name:  p.name  || p.user?.name  || '',
      email: p.email || p.user?.email || '',
      hasOverride:  !!override,
      overrideType: override ? 'user' : null,
      modules:      override ? override.modules : null,
    };
  });
  const filtered = people.filter(p => {
    // For teachers: filter by section if selected (assignedSections is returned from backend)
    if (filterSec && tab === 'teacher') {
      const secs = p.assignedSections || [];
      if (!secs.includes(filterSec)) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s) ||
           p.admissionNo?.toLowerCase().includes(s) || p.employeeId?.toLowerCase().includes(s);
  });

  // For teacher tab: use class definition sections so dropdown doesn't change when section is selected
  // For student tab: same
  const sections = filterClass
    ? (classes.find(c => String(c._id||c.id) === String(filterClass))?.sections || [])
        .map(s => typeof s === 'string' ? s : s.name || String(s))
    : [];

  if (loading) return (
    <div className="ap"><style>{style}</style>
      <div className="ap-loading"><div className="spinner"/>Loading…</div>
    </div>
  );

  return (
    <div className="ap"><style>{style}</style>
      <div className="ap-header">
        <div>
          <div className="ap-title">Access Control Panel</div>
          <div className="ap-sub">Manage which modules teachers, students, finance, coordinators, librarians and hostel members can access</div>
        </div>
      </div>

      {/* Role tabs */}
      <div className="ap-tabs">
        <button className={`ap-tab ${tab==='teacher'?'active':''}`} onClick={() => { setTab('teacher'); setSubTab('role'); setOpenDrawer(null); }}>
          👨‍🏫 Teachers
        </button>
        <button className={`ap-tab ${tab==='student'?'active':''}`} onClick={() => { setTab('student'); setSubTab('role'); setOpenDrawer(null); }}>
          🎓 Students
        </button>
        <button className={`ap-tab ${tab==='finance'?'active':''}`} onClick={() => { setTab('finance'); setSubTab('role'); setOpenDrawer(null); }}>
          💰 Finance
        </button>
        <button className={`ap-tab ${tab==='coordinator'?'active':''}`} onClick={() => { setTab('coordinator'); setSubTab('role'); setOpenDrawer(null); }}>
          🧑‍💼 Coordinator
        </button>
        <button className={`ap-tab ${tab==='librarian'?'active':''}`} onClick={() => { setTab('librarian'); setSubTab('role'); setOpenDrawer(null); }}>
          📚 Library
        </button>
        <button className={`ap-tab ${tab==='hostel_member'?'active':''}`} onClick={() => { setTab('hostel_member'); setSubTab('role'); setOpenDrawer(null); }}>
          🏨 Hostel
        </button>
        <button className={`ap-tab ${tab==='requests'?'active':''}`} onClick={() => { setTab('requests'); setSubTab('none'); setOpenDrawer(null); }}>
          📋 Requests
        </button>
        <button className={`ap-tab ${tab==='checkin'?'active':''}`} onClick={() => { setTab('checkin'); setSubTab('none'); setOpenDrawer(null); }}>
          ⏱️ Check-in
        </button>
      </div>

      {tab==='checkin' && (
        <div style={{maxWidth:640,background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'1.2rem 1.4rem'}}>
          <div style={{fontSize:'.84rem',color:'#6b7194',marginBottom:16}}>
            Let staff (yourself, teachers, coordinators, finance) check in and out for the day directly from their dashboard.
          </div>
          {!access?.checkinOwnerAllowed ? (
            <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:10,padding:'12px 14px',fontSize:'.8rem',color:'#92400e'}}>
              🔒 Your school owner hasn't granted permission for this branch to enable self check-in yet. Ask them to turn it on from their branch settings.
            </div>
          ) : (
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 0'}}>
              <div>
                <div style={{fontSize:'.86rem',fontWeight:700,color:'#0f1123'}}>Staff Self Check-in / Check-out</div>
                <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:2}}>
                  {access?.checkinAdminEnabled ? 'Enabled — everyone can check in/out from their dashboard.' : 'Disabled — the check-in button is inactive for everyone.'}
                </div>
              </div>
              <label style={{position:'relative',display:'inline-block',width:44,height:24,flexShrink:0}}>
                <input type="checkbox" checked={!!access?.checkinAdminEnabled} disabled={saving} style={{opacity:0,width:0,height:0}}
                  onChange={async e => {
                    const enabled = e.target.checked;
                    setSaving(true);
                    try {
                      await api.put('/admin/access/checkin', { enabled });
                      setAccess(prev => ({ ...prev, checkinAdminEnabled: enabled }));
                      toast.success(`Staff self check-in ${enabled ? 'enabled' : 'disabled'}`);
                    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update'); }
                    finally { setSaving(false); }
                  }} />
                <span style={{position:'absolute',inset:0,background:access?.checkinAdminEnabled?'#10c98e':'#d1d5db',borderRadius:99,transition:'.15s',cursor:saving?'not-allowed':'pointer'}}>
                  <span style={{position:'absolute',height:18,width:18,left:access?.checkinAdminEnabled?23:3,top:3,background:'white',borderRadius:'50%',transition:'.15s'}}/>
                </span>
              </label>
            </div>
          )}
        </div>
      )}

      {tab==='requests' && (
        <div style={{maxWidth:640,background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'1.2rem 1.4rem'}}>
          <div style={{fontSize:'.84rem',color:'#6b7194',marginBottom:16}}>
            Control whether coordinator and finance actions must be approved by an admin first, or take effect immediately.
          </div>
          {[
            { role:'coordinator', label:'Coordinator', icon:'🧑‍💼', key:'coordinatorDirectAction' },
            { role:'finance',     label:'Finance',     icon:'💰', key:'financeDirectAction' },
          ].map(r => (
            <div key={r.role} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 0',borderBottom:'1px solid #f0f2f8'}}>
              <div>
                <div style={{fontSize:'.86rem',fontWeight:700,color:'#0f1123'}}>{r.icon} {r.label}</div>
                <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:2}}>
                  {access?.[r.key] ? 'Actions apply immediately — no admin approval needed.' : 'Actions are submitted as requests and require admin approval.'}
                </div>
              </div>
              <label style={{position:'relative',display:'inline-block',width:44,height:24,flexShrink:0}}>
                <input type="checkbox" checked={!!access?.[r.key]} disabled={saving} style={{opacity:0,width:0,height:0}}
                  onChange={async e => {
                    const directAction = e.target.checked;
                    setSaving(true);
                    try {
                      await api.put('/admin/access/request-mode', { role: r.role, directAction });
                      setAccess(prev => ({ ...prev, [r.key]: directAction }));
                      toast.success(`${r.label} requests ${directAction ? 'disabled — actions now apply directly' : 'enabled'}`);
                    } catch { toast.error('Failed to update'); }
                    finally { setSaving(false); }
                  }} />
                <span style={{position:'absolute',inset:0,background:access?.[r.key]?'#10c98e':'#d1d5db',borderRadius:99,transition:'.15s',cursor:saving?'not-allowed':'pointer'}}>
                  <span style={{position:'absolute',height:18,width:18,left:access?.[r.key]?23:3,top:3,background:'white',borderRadius:'50%',transition:'.15s'}}/>
                </span>
              </label>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tabs */}
      {tab!=='requests' && tab!=='checkin' && <div className="sub-tabs">
        {[
          { key:'role',       label:`Default (All ${ROLE_LABEL[tab]})` },
          ...(tab === 'teacher' || tab === 'student' ? [{ key:'class', label:'By Class / Section' }] : []),
          { key:'individual', label:'Individual Override' },
        ].map(st => (
          <button key={st.key} className={`sub-tab ${subTab===st.key?'active':''}`}
            onClick={() => { setSubTab(st.key); setOpenDrawer(null); }}>
            {st.label}
          </button>
        ))}
      </div>}

      {/* ── ROLE DEFAULTS ──────────────────────────────────────────────────── */}
      {subTab === 'role' && (
        <div className="role-card">
          <div className="role-card-head">
            <div>
              <div className="role-card-title">
                Default Access — All {ROLE_LABEL[tab]}
              </div>
              <div className="role-card-sub">
                {tab === 'teacher' || tab === 'student'
                  ? 'These apply to everyone unless overridden by class/section or individual settings'
                  : 'These apply to everyone unless overridden by individual settings'}
              </div>
            </div>
            <div style={{display:'flex', gap:8}}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAllRole(false)}>Disable All</button>
              <button className="btn btn-blue btn-sm" onClick={() => setAllRole(true)}>Enable All</button>
            </div>
          </div>
          <div className="module-grid">
            {modules.map(m => {
              const enabled = roleModules.includes(m.key);
              return (
                <div key={m.key} className={`module-item ${enabled?'enabled':'disabled'}`}>
                  <div className="module-item-left">
                    <span className="module-icon">{m.icon}</span>
                    <div>
                      <div className="module-label">{m.label}</div>
                      <div className="module-desc">{m.desc}</div>
                    </div>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={enabled} onChange={() => toggleRoleModule(m.key)} />
                    <span className="toggle-slider" />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CLASS / SECTION OVERRIDES ───────────────────────────────────────── */}
      {subTab === 'class' && (
        <>
          <div style={{fontSize:'.8rem',color:'#6b7194',marginBottom:'1rem',background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:10,padding:'10px 14px'}}>
            📌 Class/section overrides apply to all students in that class or section. They take priority over role defaults, but individual overrides take priority over these.
          </div>
          {classes.length === 0 ? (
            <div className="ap-empty">No classes found.</div>
          ) : classes.map(cls => {
            const clsSections = (cls.sections || []).map(s => typeof s === 'string' ? s : s.name || String(s));
            // whole-class override
            const clsKey = `${cls._id||cls.id}-`;
            const clsOverride = getClassOverride(cls._id||cls.id, '');
            const isClsOpen = openClass === clsKey;

            return (
              <div key={cls._id||cls.id} className="class-section">
                {/* Class-wide row */}
                <div className="class-section-head" onClick={() => setOpenClass(isClsOpen ? null : clsKey)}>
                  <div>
                    <div className="cs-name">{cls.name}</div>
                    <div className="cs-meta">
                      {clsSections.length} section{clsSections.length!==1?'s':''} · {' '}
                      {clsOverride
                        ? <span style={{color:'#f59e0b',fontWeight:600}}>⚠ Custom override ({clsOverride.modules.length} modules)</span>
                        : <span style={{color:'#9094a8'}}>Using role defaults</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <span style={{fontSize:'.72rem',color:'#3b6ef8',fontWeight:600}}>
                      {isClsOpen ? 'Close' : 'Configure'}
                    </span>
                    <ChevronDownIcon style={{width:15,height:15,stroke:'#9094a8',transform:isClsOpen?'rotate(180deg)':'none',transition:'transform .2s'}} />
                  </div>
                </div>

                {isClsOpen && (
                  <div className="override-drawer">
                    <div className="od-title">All sections of {cls.name}</div>
                    <div className="od-module-grid">
                      {modules.map(m => {
                        const cur = classDraft[clsKey] ?? (clsOverride?.modules || roleModules);
                        const on = cur.includes(m.key);
                        return (
                          <div key={m.key} className={`od-module ${on?'on':'off'}`}
                            onClick={() => setClassDraft(prev => {
                              const c = prev[clsKey] ?? (clsOverride?.modules || [...roleModules]);
                              return { ...prev, [clsKey]: c.includes(m.key) ? c.filter(k=>k!==m.key) : [...c, m.key] };
                            })}>
                            <span className="dot"/>
                            <span>{m.icon} {m.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="od-actions">
                      <button className="btn btn-blue btn-sm" onClick={() => {
                        const mods = classDraft[clsKey] ?? (clsOverride?.modules || [...roleModules]);
                        saveClassOverride(cls._id||cls.id, '', mods);
                        setClassDraft(prev => { const n={...prev}; delete n[clsKey]; return n; });
                        setOpenClass(null);
                      }}>Save Override</button>
                      {clsOverride && (
                        <button className="btn btn-ghost btn-sm" onClick={() => { removeClassOverride(cls._id||cls.id, ''); setOpenClass(null); }}>
                          <ArrowPathIcon />Reset to Default
                        </button>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={() => setOpenClass(null)}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Per-section rows */}
                {clsSections.map(sec => {
                  const secKey = `${cls._id||cls.id}-${sec}`;
                  const secOverride = getClassOverride(cls._id||cls.id, sec);
                  const isSecOpen = openClass === secKey;
                  return (
                    <div key={sec} style={{borderTop:'1px solid #f3f5fb'}}>
                      <div className="class-section-head" style={{paddingLeft:32,background:'#fafbff'}}
                        onClick={() => setOpenClass(isSecOpen ? null : secKey)}>
                        <div>
                          <div className="cs-name" style={{fontSize:'.8rem'}}>Section {sec}</div>
                          <div className="cs-meta">
                            {secOverride
                              ? <span style={{color:'#f59e0b',fontWeight:600}}>⚠ Custom override ({secOverride.modules.length} modules)</span>
                              : clsOverride
                              ? <span style={{color:'#8b5cf6'}}>Using class override</span>
                              : <span>Using role defaults</span>}
                          </div>
                        </div>
                        <span style={{fontSize:'.72rem',color:'#3b6ef8',fontWeight:600}}>{isSecOpen?'Close':'Configure'}</span>
                      </div>
                      {isSecOpen && (
                        <div className="override-drawer">
                          <div className="od-title">{cls.name} — Section {sec}</div>
                          <div className="od-module-grid">
                            {modules.map(m => {
                              const base = secOverride?.modules || clsOverride?.modules || roleModules;
                              const cur = classDraft[secKey] ?? base;
                              const on = cur.includes(m.key);
                              return (
                                <div key={m.key} className={`od-module ${on?'on':'off'}`}
                                  onClick={() => setClassDraft(prev => {
                                    const c = prev[secKey] ?? base;
                                    return { ...prev, [secKey]: c.includes(m.key) ? c.filter(k=>k!==m.key) : [...c, m.key] };
                                  })}>
                                  <span className="dot"/>
                                  <span>{m.icon} {m.label}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="od-actions">
                            <button className="btn btn-blue btn-sm" onClick={() => {
                              const base = secOverride?.modules || clsOverride?.modules || [...roleModules];
                              const mods = classDraft[secKey] ?? base;
                              saveClassOverride(cls._id||cls.id, sec, mods);
                              setClassDraft(prev => { const n={...prev}; delete n[secKey]; return n; });
                              setOpenClass(null);
                            }}>Save Override</button>
                            {secOverride && (
                              <button className="btn btn-ghost btn-sm" onClick={() => { removeClassOverride(cls._id||cls.id, sec); setOpenClass(null); }}>
                                <ArrowPathIcon />Reset
                              </button>
                            )}
                            <button className="btn btn-ghost btn-sm" onClick={() => setOpenClass(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}

      {/* ── INDIVIDUAL OVERRIDES ────────────────────────────────────────────── */}
      {subTab === 'individual' && (
        <>
          <div className="filter-bar">
            <div className="search-wrap">
              <MagnifyingGlassIcon />
              <input className="search-input" placeholder={`Search ${ROLE_LABEL[tab]?.toLowerCase()}…`}
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {(tab === 'teacher' || tab === 'student') && (
              <select className="filter-select" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSec(''); }}>
                <option value="">All Classes</option>
                {sortByName(classes).map(c => <option key={c._id||c.id} value={c._id||c.id}>{c.name}</option>)}
              </select>
            )}
            {(tab === 'teacher' || tab === 'student') && sections.length > 0 && (
              <select className="filter-select" value={filterSec} onChange={e => setFilterSec(e.target.value)}>
                <option value="">All Sections</option>
                {sortAsc(sections).map(s => <option key={s} value={s}>Section {s}</option>)}
              </select>
            )}
          </div>

          <div className="panel">
            <div className="panel-head">
              <div className="panel-title">
                {ROLE_LABEL[tab]} ({filtered.length})
              </div>
              <div style={{fontSize:'.72rem',color:'#9094a8'}}>
                Click a person to set individual access overrides
              </div>
            </div>

            {loading ? (
              <div className="ap-loading"><div className="spinner"/>Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="ap-empty">No {ROLE_LABEL[tab]?.toLowerCase()} found.</div>
            ) : filtered.map(person => {
              const isOpen = openDrawer === String(person.userId);
              const draft  = draftModules[String(person.userId)] || roleModules;

              return (
                <div key={person._id}>
                  <div className="person-row">
                    <div style={{display:'flex', alignItems:'center', gap:12, flex:1, minWidth:0}}>
                      <div className="p-avatar">{inits(person.name)}</div>
                      <div style={{minWidth:0}}>
                        <div className="p-name">{person.name}</div>
                        <div className="p-meta">
                          {tab === 'teacher'
                            ? `${person.email} · ${person.employeeId || ''} · ${typeof person.assignedClasses === 'string' ? person.assignedClasses : fmtClasses(person.assignedClasses)}`
                            : tab === 'student'
                            ? `${person.email} · ${person.class} Sec ${person.section} · Roll ${person.rollNo}`
                            : `${person.email} · ${person.employeeId || ''}`}
                        </div>
                      </div>
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
                      {person.hasOverride
                        ? <span className={`p-badge ${person.overrideType === 'user' ? 'override' : person.overrideType === 'section' ? 'section-ov' : 'class-ov'}`}>
                            {person.overrideType === 'user' ? '✏ Individual Override'
                              : person.overrideType === 'section' ? '📌 Section Override'
                              : '📁 Class Override'}
                          </span>
                        : <span className="p-badge default">Using Default</span>}
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => openUserDrawer(String(person.userId), person.modules || [...roleModules])}>
                        {isOpen ? 'Close' : 'Configure'}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="override-drawer">
                      <div className="od-title">Individual Access — {person.name}</div>
                      <div className="od-module-grid">
                        {modules.map(m => {
                          const on = draft.includes(m.key);
                          return (
                            <div key={m.key} className={`od-module ${on?'on':'off'}`}
                              onClick={() => toggleDraftModule(String(person.userId), m.key)}>
                              <span className="dot"/>
                              <span>{m.icon} {m.label}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10}}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({...prev,[String(person.userId)]:modules.map(m=>m.key)}))}>Select All</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({...prev,[String(person.userId)]:[]}))}>Clear All</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({...prev,[String(person.userId)]:[...roleModules]}))}>Reset to Default</button>
                      </div>
                      <div className="od-actions">
                        <button className="btn btn-blue btn-sm" onClick={() => saveUserOverride(String(person.userId))} disabled={saving}>
                          {saving ? 'Saving…' : 'Save Override'}
                        </button>
                        {person.hasOverride && person.overrideType === 'user' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => removeUserOverride(String(person.userId))}>
                            <ArrowPathIcon />Remove Override
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenDrawer(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AccessPanel;